import { useEffect, useRef } from "react";
import useScan from "../../../hooks/useScan";
import { ISearchUser } from "../../../store/types/userTypes";

type UsePLScannerListenerArgs = {
  enabled: boolean;
  onScan: (result: ISearchUser) => void;
};

/**
 * Registers a document keydown listener for the 2D HID scanner.
 * Mirrors the exact pattern used in Dashboard.tsx — the listener effect uses
 * a deps array of [enabled] so it registers once per enabled transition,
 * locking in the original scanQrPdfScanner closure whose codeScanned variable
 * persists across re-renders (same as Dashboard).
 */
const usePLScannerListener = ({ enabled, onScan }: UsePLScannerListenerArgs) => {
  const { scanQrPdfScanner, result, reset } = useScan();

  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  // Lock in the closure at the moment enabled becomes true — identical to Dashboard's [] pattern.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!enabled) return;
    // useCapture: true → el listener corre en la fase de captura, ANTES de que
    // los eventos sintéticos de React (bubble) lleguen al componente. Esto permite
    // que stopImmediatePropagation bloquee el onKeyDown del input cuando se detecta
    // un burst del scanner, evitando que handleManualSearch se dispare con basura.
    document.addEventListener("keydown", scanQrPdfScanner, true);
    return () => {
      document.removeEventListener("keydown", scanQrPdfScanner, true);
    };
  }, [enabled]);

  useEffect(() => {
    if (result) {
      onScanRef.current(result);
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);
};

export default usePLScannerListener;
